/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import chalk from 'chalk';
import { ConfigException } from '_core/exceptions';
import { getConstants, updateConstants } from '_core/helpers/constants';
import { Auth0, FileEnvironmentApi, Launchdarkly } from '_core/integration/';
import {
  APIEnvs,
  ChannelType,
  IFinalResponse,
  IInitializeReturn,
  InitializeTypes,
} from '../types';
import { Bundle, isNull, isValue } from '../util';
import AxiosHelper from './api/AxiosHelper';
import { updateEndpoints } from './api/Endpoints';
import { Logger } from './logger';
import { cleanMocks } from './mock_api';

export type TypeOfAuth0 = {
  client_id: string;
  client_secret: string;
  audience: string;
  grant_type: string;
};

export const initialize = async (
  config: InitializeTypes,
  preWeb?: boolean,
): Promise<IInitializeReturn> => {
  const dtNow = new Date();
  let countErrors = 0;
  const retorno: IInitializeReturn = {
    success: true,
    environment: config.service.environment,
    channel: config.channel,
    isMock: false,
    isMockPartially: false,
    lstConstants: null,
    lstReady: [],
    lstMocksEnabled: [],
    timeStart: 0,
  };
  cleanMocks();
  Bundle.clearValue('mockAdapter');
  Bundle.clearValue('service-endpoint');
  Bundle.clearValue('service-environment');

  if (config.service.environment === 'MOCK') {
    retorno.isMock = true;
  }

  Bundle.setValue('config', config);

  /** Validando o Objeto de Configuração */

  try {
    validateInitialize(config);
    retorno.lstReady.push({
      name: 'validateConfig',
      success: true,
      errorMessage: '',
    });
  } catch (error) {
    retorno.lstReady.push({
      name: 'validateConfig',
      success: false,
      errorMessage: error.message,
    });
    countErrors++;
  }

  /** Fez Necessário remover a lib do BugSnag por incompatibilidade entre web e app
   * const environment = getEnvironmentFromConfig(config);
   * BugsnagHelper.configure(config.bugsnag, environment);
   * */

  /** Configurando o Axios e os Mocks caso necessário */

  try {
    const lstMocksEnabled = AxiosHelper.configure(config);
    retorno.lstReady.push({
      name: 'axios',
      success: true,
      errorMessage: '',
    });
    if (config.service.environment == 'MOCK') {
      retorno.isMock = true;
      retorno.lstReady.push({
        name: 'mock',
        success: true,
        errorMessage: '',
      });
      retorno.lstMocksEnabled = lstMocksEnabled;
    } else if (lstMocksEnabled.length > 0) {
      retorno.isMockPartially = true;
      retorno.lstReady.push({
        name: 'mock',
        success: true,
        errorMessage: '',
      });
      retorno.lstMocksEnabled = lstMocksEnabled;
    }
  } catch (error) {
    let errorAxios = '';
    let errorMock = '';

    if (error.resource == 'axios') {
      errorAxios = error.message;
    } else if (error.resource == 'mock') {
      errorMock = error.message;
    }
    retorno.lstReady.push({
      name: 'axios',
      success: false,
      errorMessage: errorAxios,
    });
    retorno.lstReady.push({
      name: 'mock',
      success: false,
      errorMessage: errorMock,
    });
    countErrors++;
  }

  let isSuccessEnvsAndConst = false;
  /** Configurando As Constantes e Endpoints do launchdarkly */
  try {
    const configureEnvsAndConstStatus = await configureEnvsAndConst(
      config.service.environment,
      config.channel,
    );
    isSuccessEnvsAndConst = configureEnvsAndConstStatus ? true : false;

    retorno.lstReady.push({
      name: 'EndPoinstConstantsLaunchdarkly',
      success: isSuccessEnvsAndConst,
      errorMessage: '',
    });
    retorno.environment = configureEnvsAndConstStatus;
  } catch (error) {
    // TODO Lançar um erro no BugSnag relatando o erro dos Envs e Constants vindo do L.D
    retorno.lstReady.push({
      name: 'EndPoinstConstantsLaunchdarkly',
      success: false,
      errorMessage: error.message,
    });
    countErrors++;
  }

  /** Configurando Token não logado */
  if (config.service.environment !== 'MOCK') {
    if (isSuccessEnvsAndConst) {
      try {
        await configureTokenAuth0();
        retorno.lstReady.push({
          name: 'Auth0tokenCredentials',
          success: true,
          errorMessage: '',
        });
      } catch (error) {
        retorno.lstReady.push({
          name: 'Auth0tokenCredentials',
          success: false,
          errorMessage: error.message,
        });
        countErrors++;
      }
    } else {
      retorno.lstReady.push({
        name: 'Auth0tokenCredentials',
        success: false,
        errorMessage: 'Envs e Constant not Found',
      });
      countErrors++;
    }
    if (config.receiveConstants) {
      retorno.lstConstants = getConstants();
    }
  }

  if (countErrors !== 0) {
    retorno.success = false;
  } else {
    if (!preWeb) {
      generateMsgConfigSuccess(config);
    }
  }

  retorno.timeStart = calculateTime(dtNow);
  return retorno;
};

type tokenDataType = {
  accessToken: string;
};

export const saveTokenAuthUser = (tokenData: tokenDataType) => {
  AxiosHelper.saveTokens(JSON.stringify(tokenData), 'TOKEN_AUTH_USER');
};

export const removeTokenAuthUser = () => {
  AxiosHelper.removeTokens('TOKEN_AUTH_USER');
};

export const saveTokenSmiles = (token: string) => {
  AxiosHelper.saveSmilesToken(token);
};

export const removeTokenSmiles = () => {
  AxiosHelper.removeSmilesToken();
};

const configureEnvsAndConst = async (
  environment: APIEnvs,
  channel: ChannelType,
) => {
  try {
    let environmentLD = environment.valueOf();
    if (environment === 'PRD') {
      const fileEnvironmentApiResponse = await FileEnvironmentApi.call();
      if (
        fileEnvironmentApiResponse.success &&
        fileEnvironmentApiResponse.data
      ) {
        environmentLD = fileEnvironmentApiResponse.data;
      }
    }

    const request = {
      user: environmentLD.toUpperCase(),
      flag: 'api-data',
    };
    const launchdarklyResponse = await Launchdarkly.call(request);
    if (launchdarklyResponse.success && launchdarklyResponse.data) {
      const { constants, endpoints } = launchdarklyResponse.data;
      if (constants) {
        const constantsChannel = constants[channel];
        if (constantsChannel) {
          updateConstants(environment, constantsChannel);
          configureTokens(constantsChannel);
        } else {
          throw new ConfigException({
            message: `Dados não encontrados para o canal ${channel}`,
            resource: 'EndPoinstConstantsLaunchdarkly',
          });
        }
      }
      if (environment !== 'MOCK' && endpoints) {
        updateEndpoints(environment, endpoints);
      }
      return environmentLD;
    } else {
      throw new ConfigException({
        message: 'Dados estão vazios',
        resource: 'EndPoinstConstantsLaunchdarkly',
      });
    }
  } catch (error) {
    Logger.error(error.message);
    throw new ConfigException({
      message: error.message,
      resource: 'EndPoinstConstantsLaunchdarkly',
    });
  }
};

const configureTokens = (dados: any) => {
  if (isValue(dados['x-api-key'])) {
    const xApiKey = dados['x-api-key'];
    AxiosHelper.saveTokens(JSON.stringify(xApiKey), 'TOKEN_X_API_KEY');
  }
};

const configureTokenAuth0 = async () => {
  try {
    const key = 'auth0MtoM';
    const constants = getConstants(key);
    let auth0Result: IFinalResponse = {
      success: false,
      data: null,
      errors: undefined,
      metadata: null,
    };

    if (constants && constants[key]) {
      const keysAuth0 = constants[key];
      if (keysAuth0 && keysAuth0 !== null) {
        const request = {
          client_id: keysAuth0.client_id,
          client_secret: keysAuth0.client_secret,
          audience: keysAuth0.audience,
          grant_type: keysAuth0.grant_type,
        };
        auth0Result = await Auth0.call(request);
      }
    }

    if (auth0Result.success) {
      AxiosHelper.saveTokens(
        JSON.stringify(auth0Result.data),
        'TOKEN_CREDENTIALS',
      );
    } else {
      let msgError = '';
      let errorCode = '';
      if (auth0Result.errors && auth0Result.errors.length > 0) {
        const errorAuth0 = auth0Result.errors[0];
        msgError = errorAuth0?.errorMessage ? errorAuth0?.errorMessage : '';
        errorCode = errorAuth0?.errorCode
          ? errorAuth0?.errorCode?.toString()
          : '';
      }
      throw new ConfigException({
        message: msgError,
        errorCode: errorCode,
        resource: 'Auth0tokenCredentials',
      });
    }
  } catch (error) {
    Logger.error(error.message);
    throw new ConfigException({
      message: error.message,
      resource: 'Auth0tokenCredentials',
    });
  }
};

const generateMsgConfigSuccess = (config: InitializeTypes) => {
  let msg = chalk.green(
    '\n====================================================\n\n',
  );
  msg += chalk.yellow(
    'Smiles React Services Generic successfully configured !!!\n',
  );
  msg += chalk.yellow(`Ambiente : ${config.service.environment} \n`);
  msg += chalk.green(
    '\n====================================================\n\n',
  );
  Logger.log(msg);
};

const validateInitialize = (config: InitializeTypes) => {
  if (isNull(config)) {
    throw new Error('Config not found');
  }
  if (isNull(config.service)) {
    throw new Error(
      "service not found, Example service : { environment: 'DEV', logger: true } ",
    );
  }
  if (isNull(config.service.environment)) {
    throw new Error('service:environment not found, Example (DEV1,DEV2,HML1)');
  }
  if (isNull(config.channel)) {
    throw new Error('channel not found, Example (APP,WEB)');
  }
  if (isNull(config.loggerXstate)) {
    throw new Error(
      'loggerXstate not found, Example loggerXstate : true or false',
    );
  }
};

const calculateTime = (timeStart: Date) => {
  let milliseconds = 0;
  try {
    const end = new Date();
    milliseconds = end.getTime() - timeStart.getTime();
  } catch (error) {
    Logger.info(error);
  }
  return milliseconds;
};
